<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';

import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import type { AiModel } from '@/services/ai_models';

defineProps<{
    models: AiModel[];
    loading: boolean;
    loadingUpdateOrder?: boolean;
    actionsLabel?: string;
    savingIds?: Set<string>;
}>();

const emit = defineEmits<{
    'toggle-model': [model: AiModel, enabled: boolean];
}>();

function onToggle(model: AiModel, enabled: boolean) {
    emit('toggle-model', model, enabled);
}
</script>

<template>
    <div class="border rounded-xl overflow-hidden">
        <div
            v-if="loading"
            class="w-full py-14 flex items-center justify-center text-muted-foreground"
        >
            <Loader2 class="animate-spin h-5 w-5 mr-2" />
            Loading AI models...
        </div>

        <div v-else class="relative">
            <div
                v-if="loadingUpdateOrder"
                class="absolute inset-0 flex items-center justify-center bg-primary30/70 z-10 text-muted-foreground"
            >
                <Loader2 class="animate-spin h-5 w-5 mr-2" />
                Updating order...
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Input ($/1M)</TableHead>
                        <TableHead>Output ($/1M)</TableHead>
                        <TableHead
                            v-if="
                                models.some(
                                    (model) => model.allowed !== undefined
                                )
                            "
                            class="text-right"
                        >
                            Enabled
                        </TableHead>
                        <TableHead v-if="$slots.actions" class="text-right">
                            {{ actionsLabel ?? 'Actions' }}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow v-for="model in models" :key="model.model_id">
                        <TableCell class="font-medium">
                            {{ model.model_id }}
                        </TableCell>
                        <TableCell>{{ model.provider }}</TableCell>
                        <TableCell>{{ model.model_label }}</TableCell>
                        <TableCell>{{ model.input_tokens_price }}</TableCell>
                        <TableCell>{{ model.output_token_price }}</TableCell>
                        <TableCell
                            v-if="model.allowed !== undefined"
                            class="text-right"
                        >
                            <Switch
                                :model-value="model.allowed"
                                :disabled="savingIds?.has(model.model_id)"
                                size="xs"
                                @update:model-value="onToggle(model, $event)"
                            />
                        </TableCell>
                        <TableCell v-if="$slots.actions" class="text-right">
                            <slot name="actions" :model="model" />
                        </TableCell>
                    </TableRow>
                    <TableRow v-if="models.length === 0">
                        <TableCell
                            :colspan="6"
                            class="text-center text-muted-foreground py-10"
                        >
                            No AI models configured yet.
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    </div>
</template>
